import { FC, memo, useCallback, useEffect, useState } from 'react';
import { EventFlowBase } from './EventFlowBase';
import { useCreateEventContext } from '../../../contexts/organizer/create-event/CreateEventContext';
import { CalendarEventsFlexible } from '../unpublished-events/CalendarEventsFlexible';
import { TournamentCard } from '../../cards/tournament/TournamentCard';
import { queryEventsClient } from '../../../util/tournaments/queryEventsClient';
import { EventsFetcher } from '../../../../functions/src/util/tournaments/EventsFetcher';
import { MS_PER_DAY } from '../../../contexts/EventsLazyContext';
import { toDateString } from '../../../../functions/src/util/dates/Dates';
import { Box } from '@mui/system';

const EVENT_TYPE_TO_CALENDAR_PROPS = {
  Tournament: {
    Card: TournamentCard,
  },
};

export const CopyEvent: FC = memo(function CopyEventUnmemoized() {
  const { eventCreationSettings, event, setEvent } = useCreateEventContext();
  const { eventType } = eventCreationSettings;
  const [eventsInitial, setEventsInitial] = useState({});

  const eventsQuery = useCallback(
    (ids: 'ALL' | string[], startDate: Date, endDate: Date) => {
      return queryEventsClient('ALL', startDate, endDate, eventType);
    },
    [eventType],
  );

  useEffect(() => {
    const handler = async () => {
      const eventsFetcher = new EventsFetcher(eventsQuery);
      const startDate = toDateString(new Date(Date.now() - MS_PER_DAY * 14));
      const endDate = toDateString(new Date(Date.now() + MS_PER_DAY * 21));
      setEventsInitial(
        await eventsFetcher.fetch(
          [event?.gameId as string],
          startDate,
          endDate,
        ),
      );
    };
    handler();
  }, [event?.gameId, eventsQuery]);

  const onSelectEvent = useCallback(
    (eventId: string) => {
      setEvent((prev) => {
        return {
          ...prev,
          id: eventId,
        };
      });
    },
    [setEvent],
  );

  const getIsSelected = (eventId: string) => {
    return eventId === event?.id;
  };

  const isEventSelected = !!event?.id;

  return (
    <EventFlowBase
      title={`Copy ${eventType}`}
      subtitle={`Select a ${eventType} to copy details from`}
      Content={
        <Box display="flex" overflow="hidden" maxWidth="100%">
          <CalendarEventsFlexible
            height={'700px'}
            initialByDay={eventsInitial}
            {...EVENT_TYPE_TO_CALENDAR_PROPS[String(eventType)]}
            query={eventsQuery}
            gameIds={'ALL'}
            Wrapper={Box}
            wrapperProps={({ id }) => {
              const isSelected = getIsSelected(id);
              return {
                onClick: () => {
                  return onSelectEvent(id);
                },
                sx: {
                  transition:
                    'background-color 0.2s ease, border-color 0.2s ease',
                  border: '1px solid',
                  borderColor: isSelected ? '#add8e6' : 'transparent',
                  backgroundColor: isSelected ? '#fafafa' : 'inherit',
                  borderRadius: '10px',
                  '&:hover': {
                    cursor: 'pointer',
                    borderColor: '#add8e6',
                    backgroundColor: '#fafafa',
                  },
                  '& > *': {
                    'pointer-events': 'none',
                  },
                },
              };
            }}
          />
        </Box>
      }
      nextPageKey={'Create Event'}
      continueText={`Copy ${eventType}`}
      continueDisabled={!isEventSelected}
    />
  );
});
